import { CardStatRecord } from "./db";
import { AnswerRecord, HskEntry } from "./types";

export type NeedsWorkItem = {
  card: HskEntry;
  timesCorrect: number;
  timesIncorrect: number;
  missRate: number;
};

export const sessionScoreParts = (
  cards: HskEntry[],
  answers: AnswerRecord[]
): { right: number; wrong: number; unanswered: number; pct: number } => {
  const byOrder = new Map(answers.map((entry) => [entry.order, entry.answer]));
  let right = 0;
  let wrong = 0;
  cards.forEach((card) => {
    const answer = byOrder.get(card.order);
    if (answer === true) {
      right += 1;
    } else if (answer === false) {
      wrong += 1;
    }
  });
  const unanswered = Math.max(cards.length - right - wrong, 0);
  const answered = right + wrong;
  const pct = answered > 0 ? Math.round((right / answered) * 100) : 0;
  return { right, wrong, unanswered, pct };
};

export const missedCardsFrom = (cards: HskEntry[], answers: AnswerRecord[]): HskEntry[] => {
  const byOrder = new Map(answers.map((entry) => [entry.order, entry.answer]));
  return cards.filter((card) => byOrder.get(card.order) === false);
};

export const formatHskLevels = (levels: number[]): string => {
  const sorted = [...new Set(levels)].sort((a, b) => a - b);
  if (sorted.length === 0) {
    return "HSK";
  }
  if (sorted.length === 1) {
    return `HSK ${sorted[0]}`;
  }
  const contiguous = sorted.every((level, index) => index === 0 || level === sorted[index - 1] + 1);
  if (contiguous) {
    return `HSK ${sorted[0]}–${sorted[sorted.length - 1]}`;
  }
  return `HSK ${sorted.join(", ")}`;
};

export const formatSessionWhen = (finishedAt: number, now = Date.now()): string => {
  const date = new Date(finishedAt);
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const startOfThat = new Date(finishedAt).setHours(0, 0, 0, 0);
  const diffDays = Math.round((startOfToday - startOfThat) / 86400000);
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (diffDays === 0) {
    return `Today, ${time}`;
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const formatMissRate = (missRate: number): string => `${Math.round(missRate * 100)}%`;

export const rankNeedsWork = (
  stats: Array<Pick<CardStatRecord, "cardId" | "timesCorrect" | "timesIncorrect" | "lastSeenAt">>,
  cardsById: Map<string, HskEntry>,
  limit = 12
): NeedsWorkItem[] =>
  stats
    .map((stat) => {
      const card = cardsById.get(stat.cardId);
      const attempts = stat.timesCorrect + stat.timesIncorrect;
      if (!card || stat.timesIncorrect <= 0 || attempts === 0) {
        return null;
      }
      return {
        card,
        timesCorrect: stat.timesCorrect,
        timesIncorrect: stat.timesIncorrect,
        missRate: stat.timesIncorrect / attempts,
        lastSeenAt: stat.lastSeenAt
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort(
      (a, b) =>
        b.missRate - a.missRate || b.timesIncorrect - a.timesIncorrect || b.lastSeenAt - a.lastSeenAt
    )
    .slice(0, limit)
    .map(({ lastSeenAt: _lastSeenAt, ...item }) => item);

export const overallAccuracyPercent = (
  sessions: Array<{ correctCount: number; incorrectCount: number }>
): number | null => {
  const correct = sessions.reduce((sum, session) => sum + session.correctCount, 0);
  const incorrect = sessions.reduce((sum, session) => sum + session.incorrectCount, 0);
  const answered = correct + incorrect;
  if (answered === 0) {
    return null;
  }
  return Math.round((correct / answered) * 100);
};

export const sessionPercents = (
  sessions: Array<{ correctCount: number; incorrectCount: number; finishedAt: number }>
): { finishedAt: number; percent: number }[] =>
  sessions
    .filter((session) => session.correctCount + session.incorrectCount > 0)
    .map((session) => ({
      finishedAt: session.finishedAt,
      percent: Math.round(
        (session.correctCount / (session.correctCount + session.incorrectCount)) * 100
      )
    }));
